import { useState } from 'react'
import { ContactFormData, ContactFormErrors } from '@/types'
import { 
  validateEmail, 
  validatePhone, 
  validateName, 
  validateMessage,
  getEmailError,
  getPhoneError,
  getNameError,
  getMessageError,
} from '@/utils/validation'

/**
 * Form validation hook
 * Manages form state and validation
 */
export const useFormValidation = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  })

  const [errors, setErrors] = useState<ContactFormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  /**
   * Handle field change
   */
  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when user starts typing (only for fields that can have errors)
    if (field !== 'company' && errors[field as keyof ContactFormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  /**
   * Handle field blur (validate on blur)
   */
  const handleBlur = (field: keyof ContactFormData) => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }))

    validateField(field)
  }

  /**
   * Validate a single field
   */
  const validateField = (field: keyof ContactFormData): boolean => {
    const value = formData[field] || ''
    let error: string | undefined

    switch (field) {
      case 'name':
        if (!validateName(value)) {
          error = getNameError(value)
        }
        break
      case 'email':
        if (!validateEmail(value)) {
          error = getEmailError(value)
        }
        break
      case 'phone':
        // Phone is optional, only validate if provided
        if (value && !validatePhone(value)) {
          error = getPhoneError(value)
        }
        break
      case 'message':
        if (!validateMessage(value)) {
          error = getMessageError(value)
        }
        break
      // Company is optional, no validation needed
    }

    if (field !== 'company') {
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }))
    }

    return !error
  }

  /**
   * Validate all fields
   */
  const validateAll = (): boolean => {
    const fields: (keyof ContactFormData)[] = ['name', 'email', 'phone', 'message']
    let isValid = true

    fields.forEach((field) => {
      if (!validateField(field)) {
        isValid = false
      }
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }))
    })

    return isValid
  }

  /**
   * Check if form is valid (all required fields valid)
   */
  const isValid = (): boolean => {
    return (
      validateName(formData.name) &&
      validateEmail(formData.email) &&
      validateMessage(formData.message) &&
      (!formData.phone || validatePhone(formData.phone))
    )
  }

  /**
   * Reset form
   */
  const reset = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      message: '',
    })
    setErrors({})
    setTouched({})
  }

  return {
    formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    isValid: isValid(),
    reset,
  }
}
